import { useState, useRef, useEffect, useCallback } from 'react';

interface AudioState {
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;
  isEnabled: boolean;
  duration: number;
  currentTime: number;
  isLoading: boolean;
  error: string | null;
}

interface AudioControls {
  play: () => void;
  pause: () => void;
  stop: () => void;
  setVolume: (volume: number) => void;
  mute: () => void;
  unmute: () => void;
  toggle: () => void;
  enable: () => void;
  disable: () => void;
}

const STORAGE_KEYS = {
  VOLUME: 'planning-poker-music-volume',
  ENABLED: 'planning-poker-music-enabled',
  MUTED: 'planning-poker-music-muted'
};

export const useAudioPlayer = (audioSrc: string): [AudioState, AudioControls] => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // ローカルストレージから設定を読み込み
  const getStoredVolume = (): number => {
    const stored = localStorage.getItem(STORAGE_KEYS.VOLUME);
    return stored ? parseFloat(stored) : 0.7;
  };

  const getStoredEnabled = (): boolean => {
    const stored = localStorage.getItem(STORAGE_KEYS.ENABLED);
    return stored ? JSON.parse(stored) : false;
  };

  const getStoredMuted = (): boolean => {
    const stored = localStorage.getItem(STORAGE_KEYS.MUTED);
    return stored ? JSON.parse(stored) : false;
  };

  const [state, setState] = useState<AudioState>({
    isPlaying: false,
    volume: getStoredVolume(),
    isMuted: getStoredMuted(),
    isEnabled: getStoredEnabled(),
    duration: 0,
    currentTime: 0,
    isLoading: false,
    error: null
  });

  // 設定をローカルストレージに保存
  const saveSettings = useCallback((key: string, value: any) => {
    localStorage.setItem(key, JSON.stringify(value));
  }, []);

  // オーディオ要素を初期化
  // audioSrcのみに依存させる。音量/ミュートは別effectで既存要素に適用するため、
  // スライダー操作のたびに要素を作り直して再生が途切れることはない
  useEffect(() => {
    if (!audioSrc) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const audio = new Audio(audioSrc);
    audio.loop = true;
    audio.preload = 'metadata';

    const handleLoadedMetadata = () => {
      setState(prev => ({
        ...prev,
        duration: audio.duration,
        isLoading: false
      }));
    };

    const handleTimeUpdate = () => {
      setState(prev => ({
        ...prev,
        currentTime: audio.currentTime
      }));
    };

    const handleEnded = () => {
      setState(prev => ({
        ...prev,
        isPlaying: false,
        currentTime: 0
      }));
    };

    const handleError = (e: Event) => {
      console.error('Audio error:', e);
      setState(prev => ({
        ...prev,
        isLoading: false,
        isPlaying: false,
        error: '音楽ファイルの読み込みに失敗しました'
      }));
    };

    const handleLoadStart = () => {
      setState(prev => ({ ...prev, isLoading: true }));
    };

    const handleCanPlay = () => {
      setState(prev => ({ ...prev, isLoading: false }));
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);

    audioRef.current = audio;

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);

      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };
  }, [audioSrc]);

  // 音量/ミュート変更を既存のaudio要素に適用（要素を作り直さない）
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = state.isMuted ? 0 : state.volume;
      audioRef.current.muted = state.isMuted;
    }
    saveSettings(STORAGE_KEYS.VOLUME, state.volume);
    saveSettings(STORAGE_KEYS.MUTED, state.isMuted);
  }, [state.volume, state.isMuted, saveSettings]);

  const play = useCallback(() => {
    if (!audioRef.current || !state.isEnabled) return;

    const playPromise = audioRef.current.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setState(prev => ({ ...prev, isPlaying: true, error: null }));
        })
        .catch((error) => {
          console.error('Play failed:', error);
          setState(prev => ({
            ...prev,
            isPlaying: false,
            error: 'ブラウザの設定により音楽を再生できません'
          }));
        });
    }
  }, [state.isEnabled]);

  const pause = useCallback(() => {
    if (!audioRef.current) return;

    audioRef.current.pause();
    setState(prev => ({ ...prev, isPlaying: false }));
  }, []);

  // 有効/無効状態変更時の処理（pauseを参照するため定義後に置く）
  useEffect(() => {
    saveSettings(STORAGE_KEYS.ENABLED, state.isEnabled);
    if (!state.isEnabled && state.isPlaying) {
      pause();
    }
  }, [state.isEnabled, state.isPlaying, saveSettings, pause]);

  const stop = useCallback(() => {
    if (!audioRef.current) return;

    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setState(prev => ({
      ...prev,
      isPlaying: false,
      currentTime: 0
    }));
  }, []);

  const setVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    setState(prev => ({ ...prev, volume: clampedVolume }));
  }, []);

  const mute = useCallback(() => {
    setState(prev => ({ ...prev, isMuted: true }));
  }, []);

  const unmute = useCallback(() => {
    setState(prev => ({ ...prev, isMuted: false }));
  }, []);

  const toggle = useCallback(() => {
    if (state.isPlaying) {
      pause();
    } else {
      play();
    }
  }, [state.isPlaying, play, pause]);

  const enable = useCallback(() => {
    setState(prev => ({ ...prev, isEnabled: true }));
  }, []);

  const disable = useCallback(() => {
    setState(prev => ({ ...prev, isEnabled: false }));
  }, []);

  const controls: AudioControls = {
    play,
    pause,
    stop,
    setVolume,
    mute,
    unmute,
    toggle,
    enable,
    disable
  };

  return [state, controls];
};